import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, API, authHeader } from '../lib/api';
import {
  resolveBrandLogoUrl,
  displayCompanyName,
  displayCompanyNameEn,
  displaySystemName,
} from '@gridorbit/shared';

type BrandingForm = {
  system_name: string;
  company_name: string;
  company_name_en: string;
  logo_url: string;
};

const emptyForm = (): BrandingForm => ({
  system_name: '',
  company_name: '',
  company_name_en: '',
  logo_url: '',
});

export function BrandingSettings() {
  const [form, setForm] = useState<BrandingForm>(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    setMsg('');
    apiFetch('/api/v1/platform/branding-settings')
      .then((d) => {
        if (d.code === 0 && d.data) {
          const data = d.data as Record<string, string>;
          setForm({
            system_name: data.system_name || '',
            company_name: data.company_name || '',
            company_name_en: data.company_name_en || '',
            logo_url: data.logo_url || '',
          });
        } else setMsg(d.message || '加载失败');
        setLoading(false);
      })
      .catch(() => {
        setMsg('网络错误');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    const d = await apiFetch('/api/v1/platform/branding-settings', {
      method: 'PUT',
      body: {
        system_name: form.system_name.trim(),
        company_name: form.company_name.trim(),
        company_name_en: form.company_name_en.trim(),
        logo_url: form.logo_url.trim(),
      },
    });
    setSaving(false);
    if (d.code === 0) {
      setMsg('已保存。登录页与工位端将使用新品牌配置。');
      load();
    } else setMsg(d.message || '保存失败');
  };

  const uploadLogo = async (file: File) => {
    setUploading(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const r = await fetch(API + '/api/v1/platform/branding/logo', {
        method: 'POST',
        headers: authHeader(false),
        body: fd,
      });
      const d = await r.json();
      if (d.code === 0 && d.data) {
        setForm((f) => ({
          ...f,
          logo_url: d.data.logo_url || f.logo_url,
          system_name: d.data.system_name ?? f.system_name,
          company_name: d.data.company_name ?? f.company_name,
          company_name_en: d.data.company_name_en ?? f.company_name_en,
        }));
        setMsg('LOGO 已上传');
      } else setMsg(d.message || '上传失败');
    } catch {
      setMsg('上传网络错误');
    }
    setUploading(false);
  };

  if (loading) return <div style={{ padding: 24 }}>加载中…</div>;

  const previewLogo = resolveBrandLogoUrl(form.logo_url);
  const previewCn = displayCompanyName(form);
  const previewEn = displayCompanyNameEn(form);
  const previewSys = displaySystemName(form, 'MOM数智平台');

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 18, fontWeight: 650, marginBottom: 8 }}>OEM 品牌定制</h2>
      <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
        上传企业 LOGO、定义系统名称与公司名称。配置后，租户/管理端登录页与工位 APK 将优先使用此处品牌。
      </p>
      {msg && (
        <div
          style={{
            marginBottom: 12,
            fontSize: 13,
            color: msg.startsWith('已') || msg.includes('上传') ? '#16a34a' : '#dc2626',
          }}
        >
          {msg}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">品牌预览</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0' }}>
          <img
            src={previewLogo}
            alt="logo"
            style={{
              height: 40,
              width: 'auto',
              maxWidth: 196,
              objectFit: 'contain',
              objectPosition: 'left center',
              background: 'transparent',
            }}
          />
          <div>
            <div style={{ fontWeight: 650, fontSize: 16 }}>{previewCn}</div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{previewEn}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{previewSys}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">LOGO</div>
        <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 12 }}>
          支持 PNG / JPG / WebP / SVG，建议正方形或横版透明底，不超过 5MB。
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.png,.jpg,.jpeg,.webp,.gif,.svg"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadLogo(f);
            e.target.value = '';
          }}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? '上传中…' : '上传 LOGO'}
          </button>
          {form.logo_url ? (
            <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{form.logo_url}</span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>未上传，使用默认图标</span>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">名称</div>
        <div className="form-group">
          <label>系统名称</label>
          <input
            className="form-input"
            value={form.system_name}
            onChange={(e) => setForm((f) => ({ ...f, system_name: e.target.value }))}
            placeholder="MOM数智平台"
          />
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
            登录页主标题；留空则用默认「MOM数智平台」
          </div>
        </div>
        <div className="form-group">
          <label>公司名称（中文）</label>
          <input
            className="form-input"
            value={form.company_name}
            onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
            placeholder="经纬循道信息"
          />
        </div>
        <div className="form-group">
          <label>公司名称（英文）</label>
          <input
            className="form-input"
            value={form.company_name_en}
            onChange={(e) => setForm((f) => ({ ...f, company_name_en: e.target.value }))}
            placeholder="GridOrbit"
          />
        </div>
      </div>

      <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>
        {saving ? '保存中…' : '保存配置'}
      </button>
    </div>
  );
}
